import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { StaleDataMarker } from "@/components/ConnectionBadge";
import { Guarded } from "@/components/Guarded";
import { ExtractionTable, type ExtractionFilter } from "@/components/fleet/ExtractionTable";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { ApiError, API_BASE, get, post, qs } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { S } from "@/lib/strings";
import { DATE_PRESETS, sinceFor, type DatePreset } from "@/lib/runFilters";
import type { DeviceSummary, ExportJobStatus, Page, TaskRow } from "@/types/fleet";

const EXPORT_POLL_MS = 1000;
const EXPORT_TIMEOUT_MS = 60_000;

/**
 * Every record the fleet has extracted.
 *
 * The columns are generated from the data rather than declared, because a task without an
 * extraction schema records whatever the model decided was relevant and no two tasks agree.
 */
export function Data() {
  const [params, setParams] = useSearchParams();
  const [exporting, setExporting] = useState(false);

  const filter: ExtractionFilter & { date: DatePreset } = useMemo(
    () => ({
      task_id: params.get("task_id") || undefined,
      device_id: params.get("device_id") || undefined,
      run_id: params.get("run_id") || undefined,
      q: params.get("q") || undefined,
      date: (params.get("date") as DatePreset) || "all",
    }),
    [params],
  );

  const patch = useCallback(
    (next: Record<string, string | undefined>) => {
      const merged = new URLSearchParams(params);
      for (const [k, v] of Object.entries(next)) {
        if (v) merged.set(k, v);
        else merged.delete(k);
      }
      setParams(merged, { replace: true });
    },
    [params, setParams],
  );

  const since = useMemo(() => sinceFor(filter.date), [filter.date]);
  const tableFilter = useMemo<ExtractionFilter>(
    () => ({
      task_id: filter.task_id,
      device_id: filter.device_id,
      run_id: filter.run_id,
      q: filter.q,
      since,
    }),
    [filter, since],
  );

  const devices = useQuery<Page<DeviceSummary>, ApiError>({
    queryKey: ["devices", "filter-options"],
    queryFn: () => get<Page<DeviceSummary>>(`/devices${qs({ limit: 200 })}`),
    staleTime: 5 * 60_000,
  });
  const tasks = useQuery<Page<TaskRow>, ApiError>({
    queryKey: qk.tasks(),
    queryFn: () => get<Page<TaskRow>>("/tasks"),
    staleTime: 5 * 60_000,
  });

  const hasFilters =
    !!filter.task_id || !!filter.device_id || !!filter.run_id || !!filter.q || filter.date !== "all";

  /**
   * Export is a server-side job, not a client-side dump of the loaded page: the point of an
   * export is the rows you have *not* scrolled to. Start it, poll it, then hand the browser the
   * authenticated URL to download.
   */
  async function runExport(format: "csv" | "json") {
    setExporting(true);
    try {
      const job = await post<{ job_id: string }>("/extractions/export", {
        format,
        filter: { ...tableFilter },
      });
      const deadline = Date.now() + EXPORT_TIMEOUT_MS;
      for (;;) {
        if (Date.now() > deadline) throw new Error(S.exportFailed);
        const status = await get<ExportJobStatus>(`/exports/${job.job_id}`);
        // A finished job answers with the file itself, so a JSON body means still working.
        if (status.status === "DONE") break;
        if (status.status === "FAILED") throw new Error(status.error || S.exportFailed);
        await new Promise((r) => setTimeout(r, EXPORT_POLL_MS));
      }
      window.location.assign(`${API_BASE}/exports/${job.job_id}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : S.exportFailed);
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <PageHeader
        title={S.dataTitle}
        hint={
          <>
            {S.dataHint}
            <StaleDataMarker className="ml-1" />
          </>
        }
        actions={
          <>
            <Guarded permission="export_data">
              <Button
                size="sm"
                variant="ghost"
                disabled={exporting}
                onClick={() => void runExport("csv")}
              >
                <Download size={13} />
                {exporting ? S.exporting : S.exportCsv}
              </Button>
            </Guarded>
            <Guarded permission="export_data">
              <Button
                size="sm"
                variant="ghost"
                disabled={exporting}
                onClick={() => void runExport("json")}
              >
                {S.exportJson}
              </Button>
            </Guarded>
          </>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select
          size="sm"
          aria-label={S.filterTask}
          value={filter.task_id ?? ""}
          onChange={(e) => patch({ task_id: e.target.value || undefined })}
          placeholder={S.filterTask}
          options={(tasks.data?.items ?? []).map((t) => ({ value: t.id, label: t.name }))}
        />
        <Select
          size="sm"
          aria-label={S.filterDevice}
          value={filter.device_id ?? ""}
          onChange={(e) => patch({ device_id: e.target.value || undefined })}
          placeholder={S.filterDevice}
          options={(devices.data?.items ?? []).map((d) => ({ value: d.id, label: d.label }))}
        />
        <Select
          size="sm"
          aria-label={S.filterDate}
          value={filter.date}
          onChange={(e) => patch({ date: e.target.value })}
          options={DATE_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
        />
        <Input
          className="w-48 px-2.5 py-1 text-[13px]"
          aria-label={S.searchData}
          placeholder={S.searchData}
          defaultValue={filter.q ?? ""}
          onKeyDown={(e) => {
            if (e.key === "Enter") patch({ q: (e.target as HTMLInputElement).value || undefined });
          }}
        />
        {hasFilters && (
          <Button size="sm" variant="ghost" onClick={() => setParams(new URLSearchParams())}>
            {S.clearFilters}
          </Button>
        )}
      </div>

      <ExtractionTable filter={tableFilter} emptyBody={hasFilters ? undefined : S.noDataBody} />
    </>
  );
}
